import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Plus, CalendarRange, MapPin, ArrowUpRight, Archive } from "lucide-react";

export default function AdminEventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    description: "",
    start_date: "",
    end_date: "",
    location: "",
    format: "hybrid",
    status: "draft",
    price: 0,
    max_delegates: 300,
    organization_id: "",
    banner_url: "https://images.unsplash.com/photo-1725618878496-233974f2fd59",
    currency: "INR",
  });

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => {
    load();
    api.get("/orgs").then((r) => {
      setOrgs(r.data);
      if (r.data.length && !form.organization_id) {
        setForm((f) => ({ ...f, organization_id: r.data[0].id }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        max_delegates: Number(form.max_delegates),
        start_date: new Date(form.start_date).toISOString(),
        end_date: new Date(form.end_date).toISOString(),
      };
      await api.post("/events", payload);
      setOpen(false);
      await load();
      toast.success("Event created");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Conference Manager
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
              Events
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border transition-colors ${
                showArchived
                  ? "bg-[#0A192F] text-white border-[#0A192F]"
                  : "bg-white text-slate-700 border-slate-200 hover:border-[#0F4C81]"
              }`}
              data-testid="toggle-show-archived-events"
            >
              <Archive className="h-4 w-4" /> {showArchived ? "Showing archived" : "Show archived"}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="create-event-button">
                <Plus className="h-4 w-4 mr-1" /> New event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create a new event</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="event-form">
                {user?.role === "super_admin" && (
                  <div className="col-span-2">
                    <Label>Organisation</Label>
                    <Select value={form.organization_id} onValueChange={(v) => setForm({ ...form, organization_id: v })}>
                      <SelectTrigger data-testid="event-org-select"><SelectValue placeholder="Choose org" /></SelectTrigger>
                      <SelectContent>
                        {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="event-title-input" />
                </div>
                <div>
                  <Label>Slug</Label>
                  <Input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="event-slug-input" />
                </div>
                <div className="col-span-2">
                  <Label>Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
                </div>
                <div>
                  <Label>Start date</Label>
                  <Input type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <Label>Format</Label>
                  <Select value={form.format} onValueChange={(v) => setForm({ ...form, format: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                      <SelectItem value="hybrid">Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price (₹)</Label>
                  <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div>
                  <Label>Max delegates</Label>
                  <Input type="number" value={form.max_delegates} onChange={(e) => setForm({ ...form, max_delegates: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label>Banner URL</Label>
                  <Input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="col-span-2">
                  <Button type="submit" className="bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="event-submit-button">
                    Create event
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {events.filter((ev) => showArchived ? ev.status === "archived" : ev.status !== "archived").map((ev) => (
            <Link
              to={`/dashboard/events/${ev.id}`}
              key={ev.id}
              className={`group rounded-xl bg-white border overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all ${
                ev.status === "archived" ? "border-slate-200 opacity-60" : "border-slate-200"
              }`}
              data-testid={`admin-event-${ev.id}`}
            >
              <div
                className="h-40 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${ev.banner_url})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/80 to-transparent" />
                <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-[10px] uppercase tracking-[0.18em] font-bold">
                  {ev.status}
                </div>
                <div className="absolute bottom-3 left-4 right-4 text-white">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                    {ev.format}
                  </p>
                  <h3 className="mt-1 text-lg font-semibold">{ev.title}</h3>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <CalendarRange className="h-3.5 w-3.5" />
                  {new Date(ev.start_date).toLocaleDateString()} — {new Date(ev.end_date).toLocaleDateString()}
                </div>
                <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                  <MapPin className="h-3.5 w-3.5" />
                  {ev.location}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-[#0F4C81] font-semibold text-sm">
                    ₹{ev.price?.toLocaleString()} · cap {ev.max_delegates}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `/dashboard/events/${ev.id}/builder`; }}
                      className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 rounded bg-[#0F4C81] text-white hover:bg-[#0A3860]"
                      data-testid={`builder-${ev.id}`}
                    >
                      Builder
                    </button>
                    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
          {events.filter((ev) => showArchived ? ev.status === "archived" : ev.status !== "archived").length === 0 && (
            <div className="md:col-span-2 p-10 rounded-xl bg-white border-dashed border-2 border-slate-200 text-center" data-testid="events-empty">
              <CalendarRange className="h-6 w-6 text-slate-400 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">
                {showArchived ? "No archived events." : "No active events yet."}
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
